<template>
  <div v-if="!model.$disabled">
    <label v-if="getTitle" :for="sid" class="label">{{ getTitle }}</label>

    <v-select
      :value="viewValue"
      :options="values"
      :label="labelField"
      :clear-search-on-select="true"
      :close-on-select="true"
      :searchable="true"
      :class="borderValidationClass"
      :disabled="disabled"
      :input-id="inputIdValue"
      :multiple="isArray"
      :placeholder="placeholder"
      :taggable="taggable"
      :filterable="!ajax"
      :filter="filterFunction"
      @input="setSchemaValue"
      @search="search"
      @search:focus="focus"
      @search:blur="blur"
    >
      <template slot="no-options">
        <slot name="no-options">
          <span>{{ noOptionsText }}</span>
        </slot>
      </template>
    </v-select>

    <w-error :model="model" />
  </div>
</template>

<script>
import 'vue-select/dist/vue-select.css';
import vSelect from 'vue-select';

import modelMixin from '../mixins/modelMixin';
import WError from './error.vue';

// @vuese
// Select z autocomplete, możliwością uruchomienia jako multiselect, itp.
// @group components
export default {
  name: 'VueSelect',
  components: { WError, vSelect },
  mixins: [modelMixin],
  props: {
    // Dostępne wartości selecta
    values: { type: Array, required: true },

    // Nazwa pola używanego do wyświetlania opcji
    labelField: { type: String, default: 'text' },

    // Nazwa pola z wartością opcji dla typów prostych
    valueField: { type: String, default: 'value' },

    // Tekst wyświetlany w przypadku braku opcji
    noOptionsText: { type: String, default: 'Przepraszamy, nie znaleziono pasującej opcji.' },

    // Czy pozwolić na wprowadzanie własnych opcji
    taggable: { type: Boolean, default: false },

    // Placeholder
    placeholder: { type: String, required: false },

    // Wartości będą pobierane z API
    ajax: { type: Boolean, default: false },

    // Wartość atrybutu id inputa
    inputId: { type: String, required: false }
  },
  data() {
    return {
      sid: 'ddw' + new Date().getTime().toString() + Math.random() * 1000000
    };
  },
  computed: {
    // Wartośc atrybutu id inputa
    inputIdValue() {
      if (!!this.inputId) {
        return this.inputId;
      }
      return this.sid;
    },
    // Czy model jest tablicą
    isArray() {
      return this.model.$schema.type === 'array';
    },
    // Czy model jest obiektem
    isObject() {
      return this.model.$schema.type === 'object';
    },
    // Czy elementy tablicy są obiektami
    isItemSimple() {
      return this.isArray && this.model.$schema.itemType !== 'object';
    },
    // Aktualna wartość widoku
    viewValue() {
      let value = this.model.$model;
      if (this.isNullable(value)) {
        return null;
      }
      if (this.isArray) {
        return this.getArrayValue(value);
      }
      if (!this.isObject) {
        value = this.wrapValue(value);
      }
      return this.findValueInOptions(value) || value;
    },
    // Filterable nie wyłącza wyszukiwania, gdy aktywne jest taggable, trzeba przekazać funkcję filtrującą
    filterFunction() {
      if (this.ajax) {
        return options => options;
      }
    }
  },
  methods: {
    // Przeszukiwanie listy
    search(value) {
      this.$emit('search', value);
    },
    // Blur na polu schemy
    blur() {
      this.$emit('blur');
      this.model.$hasFocus = false;
    },
    // Focus na polu schemy
    focus() {
      this.$emit('focus');
      this.model.$hasFocus = true;
    },

    // Ustawia wartość w schemie
    setSchemaValue(value) {
      if (typeof value !== 'object' || value === null) {
        value = { [this.labelField]: value };
      }
      if (this.isArray) {
        return this.setArraySchemaValue(value);
      }
      this.model.$setData(this.isObject ? value : value[this.valueField] ?? value[this.labelField]);
      this.$emit('input', value);
    },
    // Ustawia wartość w schemie typu array
    setArraySchemaValue(values) {
      if (this.isItemSimple) {
        values = values.map(value =>
          typeof value === 'object' ? value[this.valueField] || value[this.labelField] : value
        );
      }
      this.model.$setData(values, undefined, true);
      this.$emit('input', this.model.$getData());
    },

    // Zwraca wartość modelu widoku tablicy
    getArrayValue(values) {
      return values.map(value => {
        if (this.isItemSimple) {
          value = this.wrapValue(value);
        }
        return this.findValueInOptions(value) || value;
      });
    },
    // Zwraca wybraną opcję z listy dostępnych opcji
    findValueInOptions(value) {
      return this.values.find(option => option[this.valueField] === (value[this.valueField] || value[this.labelField]));
    },
    // Opakowuje wartość prostą w obiekt
    wrapValue(value) {
      return { [this.labelField]: value };
    },

    // Czy wartość jest pusta
    isNullable(value) {
      if (typeof value === 'object' && !Array.isArray(value)) {
        return this.isObjectValueNullable(value);
      }
      return !value && value !== 0 && value !== false;
    },
    // Czy wartość obiektu jest pusta
    isObjectValueNullable(value) {
      return !value || (this.isNullable(value[this.labelField]) && this.isNullable(value[this.valueField]));
    }
  }
};
</script>

<style>
.is-warning > .vs__dropdown-toggle {
  border-color: #ffc107 !important;
}

.is-invalid > .vs__dropdown-toggle {
  border-color: #dc3545 !important;
}

.vs__search::placeholder {
  color: #6c757d;
}
</style>
